import React, { useState, useRef, useCallback } from 'react';
import axios from 'axios';
import {
  Wand2, UploadCloud, Loader2, CheckCircle2, AlertTriangle, FileText,
  FileSpreadsheet, X, Archive, SkipForward,
} from 'lucide-react';

/**
 * Universal "drop any file(s) here" uploader.
 *
 * Accepts:
 *   • One or many supported files (.pdf/.xlsx/.xls/.csv/.png/.jpg/.txt)
 *     → posted in parallel (concurrency=3) to /upload/auto/{client_id}
 *   • A single .zip archive → posted to /upload/zip/{client_id}, which unpacks
 *     and processes every member through the same Smart Upload pipeline.
 *
 * Both paths benefit from the SHA-256 dedup (re-uploads skipped) and the
 * row-level natural-key upserts (no double counting).
 *
 * The queue shows live per-file status and a final summary toast.
 */

const SUPPORTED_EXTS = ['pdf', 'xlsx', 'xls', 'csv', 'png', 'jpg', 'jpeg', 'txt'];
const ACCEPT_ATTR = '.pdf,.xlsx,.xls,.csv,.png,.jpg,.jpeg,.txt,.zip';
const CONCURRENCY = 3;

const moduleColors = {
  revenue: 'bg-green-100 text-state-success',
  expenses: 'bg-orange-100 text-orange-700',
  suppliers: 'bg-blue-100 text-state-info',
  reconciliation: 'bg-indigo-100 text-indigo-700',
  recipes: 'bg-pink-100 text-pink-700',
  menu: 'bg-yellow-100 text-state-warning',
  menu_performance: 'bg-amber-100 text-amber-700',
  mandi: 'bg-emerald-100 text-emerald-700',
  reviews: 'bg-purple-100 text-purple-700',
};

const fileIcon = (name) => {
  const ext = name.split('.').pop()?.toLowerCase();
  if (ext === 'zip') return <Archive className="h-4 w-4 text-purple-600 flex-shrink-0" />;
  if (ext === 'pdf') return <FileText className="h-4 w-4 text-rose-600 flex-shrink-0" />;
  return <FileSpreadsheet className="h-4 w-4 text-emerald-600 flex-shrink-0" />;
};

export const SmartUploadZone = ({ clientId, API, onUploadComplete }) => {
  const [queue, setQueue] = useState([]);   // [{id, file, status, ...}]
  const [busy, setBusy] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  const reset = () => {
    setQueue([]);
    if (inputRef.current) inputRef.current.value = '';
  };

  const updateItem = useCallback((id, patch) => {
    setQueue((q) => q.map((it) => (it.id === id ? { ...it, ...patch } : it)));
  }, []);

  const processFile = async (item) => {
    const { id, file } = item;
    const ext = file.name.split('.').pop()?.toLowerCase();
    const isZip = ext === 'zip';
    updateItem(id, { status: 'processing' });
    try {
      const fd = new FormData();
      fd.append('file', file);
      const url = isZip
        ? `${API}/upload/zip/${clientId}`
        : `${API}/upload/auto/${clientId}`;
      const { data } = await axios.post(url, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: isZip ? 600000 : 120000, // zip can take a while
      });
      updateItem(id, { status: 'done', result: data, isZip });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || e?.message || 'Upload failed';
      if (status === 409) {
        updateItem(id, { status: 'duplicate', error: detail });
      } else {
        updateItem(id, { status: 'failed', error: detail });
      }
    }
  };

  const runQueue = async (items) => {
    setBusy(true);
    // Simple concurrency pool (parallel up to CONCURRENCY at a time)
    const pool = [];
    let i = 0;
    const next = async () => {
      while (i < items.length) {
        const item = items[i++];
        await processFile(item);
      }
    };
    for (let p = 0; p < Math.min(CONCURRENCY, items.length); p++) pool.push(next());
    await Promise.all(pool);
    setBusy(false);
    onUploadComplete && onUploadComplete();
  };

  const handleFiles = (fileList) => {
    if (!fileList || fileList.length === 0) return;
    const incoming = Array.from(fileList);
    const accepted = [];
    const rejected = [];
    incoming.forEach((f, idx) => {
      const ext = f.name.split('.').pop()?.toLowerCase();
      const ok = ext === 'zip' || SUPPORTED_EXTS.includes(ext);
      if (ok) {
        accepted.push({
          id: `${Date.now()}-${idx}-${f.name}`,
          file: f,
          status: 'pending',
        });
      } else {
        rejected.push({
          id: `${Date.now()}-${idx}-${f.name}`,
          file: f,
          status: 'skipped',
          error: `Unsupported .${ext || 'bin'}`,
        });
      }
    });
    const allItems = [...accepted, ...rejected];
    setQueue(allItems);
    if (inputRef.current) inputRef.current.value = '';
    if (accepted.length) runQueue(accepted);
  };

  const onFileChange = (e) => handleFiles(e.target.files);
  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    handleFiles(e.dataTransfer.files);
  };

  // Summary stats
  const stats = queue.reduce(
    (acc, it) => {
      if (it.status === 'done') {
        acc.done++;
        if (it.isZip && it.result) {
          acc.zipImported += it.result.imported || 0;
          acc.zipDup += it.result.duplicates || 0;
          acc.zipFailed += it.result.failed || 0;
          acc.rows += it.result.rows_imported || 0;
        } else if (it.result) {
          // Bill-level count is the honest primary number when present
          // (e.g. bill-wise POS file → 539 bills, aggregated to 31 daily
          // rollups). Fall back to auto_imported for daily-summary files.
          const bills = it.result.transactions_written || 0;
          acc.rows += bills > 0 ? bills : (it.result.auto_imported || 0);
        }
      } else if (it.status === 'duplicate') acc.dup++;
      else if (it.status === 'failed') acc.failed++;
      else if (it.status === 'skipped') acc.skipped++;
      else if (it.status === 'processing') acc.processing++;
      else acc.pending++;
      return acc;
    },
    { done: 0, dup: 0, failed: 0, skipped: 0, processing: 0, pending: 0, rows: 0, zipImported: 0, zipDup: 0, zipFailed: 0 }
  );

  const allDone = queue.length > 0 && !busy && stats.processing === 0 && stats.pending === 0;

  return (
    <div className="bg-white border border-border rounded-md p-5" data-testid="smart-upload-zone">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2">
          <Wand2 className="h-4 w-4 text-accent" />
          <h4 className="text-sm font-semibold text-primary">Smart Upload — drop one, many, or a ZIP</h4>
        </div>
        <span className="text-[10px] uppercase tracking-wider text-text-muted font-semibold">
          PDF · Excel · CSV · Image · ZIP
        </span>
      </div>

      <p className="text-xs text-text-secondary mb-3">
        AI auto-detects each file's module (revenue, expense, supplier, recipe, menu, mandi, recon, review) and routes it to the right tab.
        Drop a single ZIP to import a whole month or year at once — duplicates are skipped automatically.
      </p>

      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        onClick={() => !busy && inputRef.current?.click()}
        className={`border-2 border-dashed rounded-md p-6 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-accent bg-background-secondary' : 'border-border hover:border-accent'
        } ${busy ? 'opacity-60 pointer-events-none' : ''}`}
        data-testid="smart-upload-dropzone"
      >
        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT_ATTR}
          multiple
          onChange={onFileChange}
          className="hidden"
          data-testid="smart-upload-input"
        />
        {busy ? (
          <div className="flex flex-col items-center space-y-2 text-text-secondary">
            <Loader2 className="h-7 w-7 animate-spin text-accent" />
            <p className="text-sm font-medium">
              Processing {stats.processing + stats.pending} of {queue.length}…
            </p>
            <p className="text-xs text-text-muted">
              {stats.done} done · {stats.dup} duplicate · {stats.failed} failed
            </p>
          </div>
        ) : (
          <div className="flex flex-col items-center space-y-2">
            <UploadCloud className="h-7 w-7 text-text-muted" />
            <p className="text-sm text-text-secondary">
              <span className="font-semibold text-accent">Click to upload</span> or drag <strong>one or many</strong> files
            </p>
            <p className="text-[11px] text-text-muted">
              Drop a <strong>.zip</strong> to import a whole month / year at once
            </p>
          </div>
        )}
      </div>

      {/* Final summary banner */}
      {allDone && (
        <div className="mt-4 bg-emerald-50 border border-emerald-200 rounded-md p-3 text-sm flex items-start justify-between gap-3" data-testid="smart-upload-summary">
          <div className="flex items-start gap-2">
            <CheckCircle2 className="h-4 w-4 text-state-success mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-semibold text-emerald-900">
                Batch complete — {stats.done} processed,{' '}
                {stats.dup > 0 && <span>{stats.dup} duplicate · </span>}
                {stats.failed > 0 && <span>{stats.failed} failed · </span>}
                {stats.skipped > 0 && <span>{stats.skipped} skipped · </span>}
                <span>{stats.rows} rows imported</span>
              </p>
              {(stats.zipImported > 0 || stats.zipDup > 0 || stats.zipFailed > 0) && (
                <p className="text-[11px] text-emerald-800 mt-0.5">
                  ZIP breakdown: {stats.zipImported} imported · {stats.zipDup} duplicate · {stats.zipFailed} failed inside archives.
                </p>
              )}
            </div>
          </div>
          <button
            onClick={reset}
            className="text-emerald-800 hover:text-emerald-900 p-1"
            data-testid="smart-upload-clear"
            aria-label="Clear queue"
            title="Clear queue"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Per-file queue */}
      {queue.length > 0 && (
        <div className="mt-3 border border-border rounded-md overflow-hidden" data-testid="smart-upload-queue">
          <div className="px-3 py-2 bg-background-secondary text-[11px] font-semibold text-text-secondary uppercase tracking-wider flex justify-between">
            <span>Upload queue · {queue.length} {queue.length === 1 ? 'item' : 'items'}</span>
            <span className="text-text-muted normal-case font-normal">
              Concurrency {CONCURRENCY}× · safe to re-drop (dupes auto-skip)
            </span>
          </div>
          <ul className="divide-y divide-border max-h-80 overflow-y-auto">
            {queue.map((it) => <QueueRow key={it.id} item={it} />)}
          </ul>
        </div>
      )}
    </div>
  );
};

const QueueRow = ({ item }) => {
  const { file, status, error, result, isZip } = item;
  return (
    <li className="px-3 py-2 flex items-center gap-3 text-xs" data-testid={`queue-row-${status}`}>
      {fileIcon(file.name)}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-text-primary truncate" title={file.name}>
            {file.name}
          </span>
          <span className="text-[10px] text-text-muted whitespace-nowrap">
            ({(file.size / 1024).toFixed(0)} KB)
          </span>
        </div>
        {status === 'done' && !isZip && result && (
          <p className="text-[11px] text-text-secondary mt-0.5">
            Routed to{' '}
            <span className={`px-1.5 py-0.5 rounded font-semibold ${moduleColors[result.detected_module] || 'bg-gray-100'}`}>
              {result.detected_module}
            </span>{' '}
            {result.transactions_written > 0 ? (
              <>· <span className="font-semibold">{result.transactions_written} bills captured</span>
                {result.auto_imported > 0 && (
                  <span className="text-text-muted"> · rolled up to {result.auto_imported} daily row{result.auto_imported === 1 ? '' : 's'}</span>
                )}</>
            ) : (
              <>· {result.auto_imported}/{result.rows_extracted} rows imported</>
            )}
            {result.pending_review > 0 && (
              <span className="text-amber-700"> · {result.pending_review} need review</span>
            )}
          </p>
        )}
        {status === 'done' && isZip && result && (
          <p className="text-[11px] text-text-secondary mt-0.5">
            {result.imported} of {result.total} files imported
            {result.duplicates > 0 && <span className="text-text-muted"> · {result.duplicates} duplicate</span>}
            {result.failed > 0 && <span className="text-rose-700"> · {result.failed} failed</span>}
            <span> · {result.rows_imported} rows</span>
          </p>
        )}
        {(status === 'duplicate' || status === 'failed' || status === 'skipped') && error && (
          <p className="text-[11px] text-text-secondary mt-0.5 truncate" title={error}>{error}</p>
        )}
      </div>
      <StatusBadge status={status} />
    </li>
  );
};

const StatusBadge = ({ status }) => {
  if (status === 'pending')
    return <span className="text-[10px] uppercase tracking-wider font-bold text-text-muted px-2 py-1 bg-slate-100 rounded">Queued</span>;
  if (status === 'processing')
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold text-accent px-2 py-1 bg-accent/10 rounded flex items-center gap-1">
        <Loader2 className="h-3 w-3 animate-spin" /> Processing
      </span>
    );
  if (status === 'done')
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold text-state-success px-2 py-1 bg-emerald-100 rounded flex items-center gap-1">
        <CheckCircle2 className="h-3 w-3" /> Imported
      </span>
    );
  if (status === 'duplicate')
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold text-amber-700 px-2 py-1 bg-amber-100 rounded flex items-center gap-1">
        <SkipForward className="h-3 w-3" /> Duplicate
      </span>
    );
  if (status === 'skipped')
    return (
      <span className="text-[10px] uppercase tracking-wider font-bold text-text-muted px-2 py-1 bg-slate-100 rounded flex items-center gap-1">
        <SkipForward className="h-3 w-3" /> Skipped
      </span>
    );
  return (
    <span className="text-[10px] uppercase tracking-wider font-bold text-state-error px-2 py-1 bg-rose-100 rounded flex items-center gap-1">
      <AlertTriangle className="h-3 w-3" /> Failed
    </span>
  );
};

export default SmartUploadZone;
